import { Injectable, inject } from '@angular/core';
import { FOUR_SECONDS } from '../../../config';
import { Actions, ofActionDispatched } from '@ngxs/store';
import { MessageBoxComponent } from '../../components';
import { MatSnackBar, MatSnackBarRef, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import {
  CloseSnackbar,
  ShowSnackbarError,
  ShowSnackbarInfo,
  ShowSnackbarSuccess,
  ShowSnackbarSummaryWarning,
  ShowSnackbarWarning,
} from './snackbar.actions';
import { SnackbarSeverity } from '../../../model/enums';
import { ActionProps, SnackbarContentModel, SnackbarData } from '../../../model';

const NO_DISMISS_DELAY = -1;

@Injectable()
export class SnackbarActionHandler {
  private readonly actions$ = inject(Actions);
  private readonly snackBar = inject(MatSnackBar);

  private snackBarRef: MatSnackBarRef<MessageBoxComponent>;

  constructor() {
    this.actions$.pipe(ofActionDispatched(ShowSnackbarInfo)).subscribe((data: SnackbarContentModel) => this.showInfo(data));
    this.actions$.pipe(ofActionDispatched(ShowSnackbarSuccess)).subscribe((data: SnackbarContentModel) => this.showSuccess(data));
    this.actions$.pipe(ofActionDispatched(ShowSnackbarWarning)).subscribe((data: SnackbarContentModel) => this.showWarning(data));
    this.actions$.pipe(ofActionDispatched(ShowSnackbarError)).subscribe((data: SnackbarContentModel) => this.showError(data));
    this.actions$
      .pipe(ofActionDispatched(ShowSnackbarSummaryWarning))
      .subscribe((data: SnackbarContentModel) => this.showSummaryWarning(data));

    this.actions$.pipe(ofActionDispatched(CloseSnackbar)).subscribe(() => this.dismiss());
  }

  showInfo(data: SnackbarContentModel): void {
    this.showMessageBox(data, SnackbarSeverity.info);
  }

  showSuccess(data: SnackbarContentModel): void {
    this.showMessageBox(data, SnackbarSeverity.success, data?.actionProps?.showClose, 'bottom', data.actionProps);
  }

  showWarning(data: SnackbarContentModel): void {
    this.showMessageBox(data, SnackbarSeverity.warning);
  }

  showError(data: SnackbarContentModel): void {
    this.showMessageBox(data, SnackbarSeverity.error, true, 'top', data.actionProps);
  }

  showSummaryWarning(data: SnackbarContentModel): void {
    this.showMessageBox(data, SnackbarSeverity.warning, true, 'top', data.actionProps);
  }

  dismiss(): void {
    if (this.snackBarRef) {
      this.snackBarRef.dismiss();
      this.snackBarRef = null;
    }
  }

  private showMessageBox(
    data: SnackbarContentModel,
    severity: SnackbarSeverity,
    showCloseButton = false,
    verticalPosition: MatSnackBarVerticalPosition = 'top',
    actionProps?: ActionProps
  ): void {
    // dismiss the previous snackbar first
    this.dismiss();

    this.snackBarRef = this.snackBar.openFromComponent(MessageBoxComponent, {
      duration: showCloseButton ? NO_DISMISS_DELAY : FOUR_SECONDS,
      horizontalPosition: 'center',
      verticalPosition,
      data: { bodyMessage: data.bodyMessage, params: data.params, severity, showCloseButton, actionProps } as SnackbarData,
    });
  }
}
